import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, ChevronUp, Check, Info } from 'lucide-react';
import InteractiveStratChart from '../../../components/InteractiveStratChart';

const CardStack = ({ queue, onSwipe, buyListCount, watchlistCount, buyListLimit, watchlistLimit, timeframe, onFinish, totalDeckSize, onToggleShortcuts }) => {
    const [revealedTicker, setRevealedTicker] = React.useState(null);
    const buyListRemaining = buyListLimit - buyListCount;
    const watchlistRemaining = watchlistLimit - watchlistCount;
    const timeframeLabels = { '1D': 'Day', '1W': 'Week', '1M': 'Month' };

    if (!queue[0]) return (
        <div className="flex flex-col items-center justify-center min-h-screen">
            {/* Simple End State */}
            <div className="text-3xl text-gray-400 font-light">🎉 You're all caught up!</div>
            <button onClick={onFinish} className="mt-8 px-8 py-3 bg-cyan-600 text-white rounded-full">View Results</button>
        </div>
    );

    const activeCard = queue[0];

    const handleSwipeAction = (direction) => {
        if (direction === 'up' && watchlistRemaining <= 0) return;
        if (direction === 'right' && buyListRemaining <= 0) return;
        setRevealedTicker(activeCard.ticker);
        setTimeout(() => { onSwipe(direction); setRevealedTicker(null); }, 800);
    };

    const handleDragEnd = (event, info) => {
        if (info.offset.x > 80) handleSwipeAction('right');
        else if (info.offset.x < -80) handleSwipeAction('left');
        else if (info.offset.y < -80) handleSwipeAction('up');
    };

    // Style adjustments for visibility
    const finishButtonStyle = "fixed bottom-24 left-1/2 -translate-x-1/2 z-50 px-8 py-3 bg-gray-900/90 text-white rounded-full font-light shadow-2xl backdrop-blur-sm hover:scale-105 active:scale-95 transition-all flex items-center gap-2";

    return (
        <div className="relative w-full min-h-screen flex items-center justify-center pt-32 pb-16">

            {/* Top Stats Bar - Positioned BELOW the Giant Header */}
            <div className="fixed top-56 left-0 right-0 z-20 flex flex-col items-center gap-6 pointer-events-none">
                {/* Universe Count & Criteria Tooltip */}
                <div className="pointer-events-auto relative group flex items-center gap-2 cursor-help px-4 py-1 rounded-full hover:bg-white/50 transition-colors">
                    <div className="text-sm font-light text-gray-500 tracking-wide">
                        {totalDeckSize} tickers in today's universe
                    </div>
                    <div className="w-4 h-4 rounded-full border border-gray-300 flex items-center justify-center text-xs text-gray-400 group-hover:border-cyan-600 group-hover:text-cyan-600 bg-white">
                        i
                    </div>
                    {/* Tooltip */}
                    <div className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 p-4 bg-gray-900/95 backdrop-blur-xl rounded-xl shadow-2xl border border-white/10 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-30 scale-95 group-hover:scale-100 duration-200">
                        <div className="text-xs font-bold text-gray-100 mb-2 border-b border-gray-700 pb-2">High-Probability Screen Criteria</div>
                        <ul className="space-y-1.5 text-xs text-gray-300 font-light">
                            <li className="flex justify-between"><span>RS {'>'} 60</span> <span className="text-cyan-400">(Very Strong)</span></li>
                            <li className="flex justify-between"><span>CR {'>'} 75%</span> <span className="text-cyan-400">(Strong Close)</span></li>
                            <li className="flex justify-between"><span>Vol {'>'} 500k</span> <span className="text-cyan-400">(Liquid)</span></li>
                            <li className="flex justify-between"><span>HVE/HV1/HVQ</span> <span className="text-cyan-400">(High Volume)</span></li>
                        </ul>
                    </div>
                </div>

                {/* Stats Pills */}
                <div className="flex gap-6 pointer-events-auto">
                    <div className="bg-white/80 backdrop-blur-md rounded-2xl px-6 py-2 shadow-sm border border-gray-200/50 flex flex-col items-center w-32">
                        <div className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Trade List</div>
                        <div className="text-2xl font-light text-gray-800">
                            {buyListCount}<span className="text-sm text-gray-300">/{buyListLimit}</span>
                        </div>
                    </div>
                    <div className="bg-white/80 backdrop-blur-md rounded-2xl px-6 py-2 shadow-sm border border-gray-200/50 flex flex-col items-center w-32">
                        <div className="text-xs text-gray-400 uppercase tracking-wider font-semibold">Watchlist</div>
                        <div className="text-2xl font-light text-gray-800">
                            {watchlistCount}<span className="text-sm text-gray-300">/{watchlistLimit}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Cards Layer */}
            <div className="relative mt-12">
                {/* Background Cards */}
                {queue[1] && <div className="absolute top-0 left-4 md:left-10 w-[90vw] md:w-[700px] h-[60vh] md:h-[800px] bg-white/60 rounded-[2.5rem] scale-[0.96] blur-[2px] border border-gray-100 shadow-xl" />}
                {queue[2] && <div className="absolute top-0 left-8 md:left-20 w-[90vw] md:w-[700px] h-[60vh] md:h-[800px] bg-white/40 rounded-[2.5rem] scale-[0.92] blur-[4px] border border-gray-100" />}

                {/* Active Card */}
                <motion.div
                    key={activeCard.id}
                    className="relative w-[90vw] md:w-[700px] h-[60vh] md:h-[800px] bg-white rounded-[2.5rem] flex flex-col overflow-hidden shadow-2xl border border-gray-100/50"
                    drag
                    dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
                    dragElastic={0.7}
                    onDragEnd={handleDragEnd}
                    initial={{ opacity: 0, scale: 0.95, y: 50 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 1.05, filter: 'blur(10px)' }}
                    transition={{ duration: 0.3 }}
                >
                    {/* Chart Area */}
                    <div className="flex-1 relative bg-[#0b1121]">
                        <InteractiveStratChart ticker={activeCard.ticker} initialData={activeCard.history} selectedTimeframe={timeframe} />

                        {/* Badges */}
                        <div className="absolute top-6 left-6 px-3 py-1 bg-white/10 backdrop-blur rounded-lg border border-white/5 text-xs text-white/70 font-mono">
                            {timeframeLabels[timeframe]}
                        </div>
                        <div className="absolute top-6 right-6 px-3 py-1 bg-white/10 backdrop-blur rounded-lg border border-white/5 text-xs text-white/70 font-mono">
                            {queue.length} LEFT
                        </div>

                        {/* Reveal Overlay */}
                        <AnimatePresence>
                            {revealedTicker && (
                                <motion.div className="absolute inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center"
                                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                    <span className="text-9xl font-thin text-white tracking-tighter">{revealedTicker}</span>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Controls Area - Center Aligned */}
                    <div className="h-28 bg-white border-t border-gray-100 flex items-center justify-center gap-8 relative px-8">



                        <button onClick={() => handleSwipeAction('left')} className="p-4 rounded-full bg-gray-50 hover:bg-red-50 hover:text-red-500 text-gray-400 transition-all hover:scale-110">
                            <ChevronLeft size={32} />
                        </button>
                        <button onClick={() => handleSwipeAction('up')} className="p-4 rounded-full bg-gray-50 hover:bg-yellow-50 hover:text-yellow-500 text-gray-400 transition-all hover:scale-110">
                            <ChevronUp size={32} />
                        </button>
                        <button onClick={() => handleSwipeAction('right')} className="p-4 rounded-full bg-gray-50 hover:bg-green-50 hover:text-green-500 text-gray-400 transition-all hover:scale-110">
                            <ChevronRight size={32} />
                        </button>
                    </div>
                </motion.div>
            </div>

            {/* Finish Button - Centered & Renamed */}
            <motion.button onClick={onFinish}
                className={finishButtonStyle}
                whileHover={{ y: -2 }}>
                <Check size={16} /> Finish & Reveal Results
            </motion.button>
        </div>
    );
};

export default CardStack;
